import { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Row,
  Col,
  Dropdown,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../styles/InvoiceTable.css";
import NewInvoiceModal from "../../../components/NewInvoiceModal";
import EditInvoiceModal from "../../../components/EditInvoiceModal";
import InvoicePdfTemplate from "../../../components/InvoicePdfTemplate";
import generatePdfBlob from "../../../components/InvoicePdfExporter";
import { toISODateOnly } from "../../../utils/dateMaker";
import Toast from "../../../utils/Toast";
import {
  FETCH_INVOICES_REQUEST,
  INVOICE_DETAIL_REQUEST,
  DELETE_INVOICE_REQUEST,
} from "../../../store/Actions/Actions";
import Axios from "../../../utils/Axios";
import { INVOICE_DETAIL } from "../../../config/API_Collection";
import InvoiceModal from "./InvoiceModal";
import InvoiceRow from "./InvoiceRow";

const InvoiceTable = ({
  invoicesProp,
  onNewInvoice,
  clientId,
  projectId,
  budgets = [],
  onSwitchTab,
}) => {
  const STATUS_LABELS = {
    0: "Draft",
    1: "Submitted",
    2: "Approved",
  };
  // Redux integration - component -> action -> saga -> API call -> reducer -> store
  const dispatch = useDispatch();
  const {
    invoices,
    isLoading,
    error,
    selectedInvoice,
    createLoading,
    deleteLoading,
    lastDeleteMessage,
  } = useSelector((state) => state.invoices);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoiceProp, setSelectedInvoiceProp] = useState(null);
  const [newInvoiceModalOpen, setNewInvoiceModalOpen] = useState(false);
  const [editInvoiceModalOpen, setEditInvoiceModalOpen] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "", triggerId: 0 });
  const [deletePrompt, setDeletePrompt] = useState({
    show: false,
    invoice: null,
  });
  const [deleteRequested, setDeleteRequested] = useState(null); // track id for success toast
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [overrideInvoices, setOverrideInvoices] = useState(null);
  const [projectFetchCompleted, setProjectFetchCompleted] = useState(false);
  const [projectFetchLoading, setProjectFetchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100); // Default 100 invoices per page
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [pdfInvoiceData, setPdfInvoiceData] = useState(null);
  const [sortBy, setSortBy] = useState(null); // "id", "date", "amount"
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" or "desc"
  const [lastInvoiceSynced, setLastInvoiceSynced] = useState(null);

  const showToast = (message, type) => {
    setToast({ message, type, triggerId: Date.now() });
  };

  const handleDownloadInvoice = async (inv) => {
    const invoiceId = inv.id || inv.invoice_id;
    setDownloadingInvoiceId(invoiceId);

    try {
      // Use the invoice data directly from the row - no need to fetch
      const invoiceData = inv;

      // Set data for hidden PDF template
      const subTotal = Number(invoiceData.amount || invoiceData.sub_total || 0);
      const gstRate = Number(invoiceData.gst || 0.15);
      const gstAmount = subTotal * gstRate;
      const total = subTotal + gstAmount;

      setPdfInvoiceData({
        invoiceNo: invoiceData.xero_no || "INV-XXXX",
        invoiceDate: invoiceData.invoice_date || invoiceData.date || "-",
        subtotal: subTotal,
        gstAmount: gstAmount,
        total: total,
        dueDate: invoiceData.due_date || invoiceData.dueDate || "-",
        clientName: invoiceData.client_name || clientName || "-",
        contactName: invoiceData.billing_contact || "-",
        clientAddress: invoiceData.client_address || "-",
        invoiceTitle: invoiceData.invoice_title || "-",
        description: invoiceData.description || "-",
      });

      // Wait for template to render
      setTimeout(async () => {
        try {
          const blob = await generatePdfBlob("invoice-table-download-template");
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const fileName = invoiceData.invoice_title
            ? `Invoice ${invoiceData.xero_no} - ${invoiceData.invoice_title}.pdf`
            : `Invoice ${invoiceData.xero_no}.pdf`;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
          setPdfInvoiceData(null);
        } catch (err) {
          console.error("PDF generation failed", err);
          showToast("Failed to generate PDF. Please try again.", "danger");
          setPdfInvoiceData(null);
        } finally {
          setDownloadingInvoiceId(null);
        }
      }, 100);
    } catch (err) {
      console.error("Failed to prepare invoice for download", err);
      showToast("Failed to download invoice. Please try again.", "danger");
      setDownloadingInvoiceId(null);
    }
  };

  // If a projectId is provided, we can call the invoices-by-project endpoint
  // which returns client_name, project_name and invoices for that project.
  useEffect(() => {
    if (!projectId) return;
    let isMounted = true;
    const fetchByProject = async () => {
      try {
        const resp = await Axios.get(
          `/invoices/invoice_list_by_project?project_id=${projectId}`
        );
        const responseData = resp?.data;
        if (!isMounted) return;

        if (responseData) {
          // Handle different response structures:
          // 1. { data: [...invoices] } - array of invoices directly in data
          // 2. { data: { client_name, project_name, invoices: [...] } } - nested structure
          const data = responseData?.data;

          setLastInvoiceSynced(data?.last_xero_sync_date || null);

          if (Array.isArray(data)) {
            // Direct array of invoices
            setOverrideInvoices(data);
          } else if (data && typeof data === "object") {
            // Nested object with invoices property
            if (data.client_name) setClientName(data.client_name);
            if (data.project_name) setProjectName(data.project_name);
            if (Array.isArray(data.invoices)) {
              setOverrideInvoices(data.invoices);
            }
          }
        }
      } catch (err) {
        // If API responds with 404 and the message indicates no invoices for the project,
        // treat it as an empty invoice list (so the UI will show "No Invoices Available").
        try {
          const status = err?.response?.status;
          const errMsg = err?.response?.data?.error || err?.message || "";
          if (status === 404 || /no invoices/i.test(errMsg)) {
            if (isMounted) setOverrideInvoices([]);
          } else {
            console.debug(
              "Could not fetch invoices-by-project",
              err?.message || err
            );
          }
        } catch (inner) {
          console.debug(
            "Could not fetch invoices-by-project",
            err?.message || err
          );
        }
      } finally {
        if (isMounted) {
          setProjectFetchCompleted(true);
          setProjectFetchLoading(false);
        }
      }
    };
    // reset flag when starting a new fetch
    setProjectFetchCompleted(false);
    setProjectFetchLoading(true);
    fetchByProject();
    return () => {
      isMounted = false;
    };
  }, [projectId]);

  // Use overrideInvoices (from invoices-by-project) if present, otherwise use Redux invoices
  const displayInvoices = Array.isArray(overrideInvoices)
    ? overrideInvoices
    : Array.isArray(invoices)
    ? invoices
    : [];

  // Sort invoices based on sortBy and sortOrder - use useMemo to prevent recalculation on every render
  const sortedInvoices = useMemo(() => {
    const arr = Array.isArray(displayInvoices) ? [...displayInvoices] : [];

    if (sortBy) {
      arr.sort((a, b) => {
        let aVal, bVal;

        if (sortBy === "id") {
          aVal = a.xero_no || a.id || "";
          bVal = b.xero_no || b.id || "";
          // String comparison for IDs
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        } else if (sortBy === "date") {
          aVal = new Date(a.invoice_date || 0).getTime();
          bVal = new Date(b.invoice_date || 0).getTime();
        } else if (sortBy === "amount") {
          aVal = Number(a.amount || 0);
          bVal = Number(b.amount || 0);
        }

        if (sortOrder === "asc") {
          return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        } else {
          return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
        }
      });
    }
    return arr;
  }, [displayInvoices, sortBy, sortOrder]);

  // Reset to first page when the data set length changes (not reference)
  const invoiceCount = displayInvoices.length;
  useEffect(() => {
    setPage(1);
  }, [invoiceCount, sortBy, sortOrder]);

  // Handle page size change
  const handlePageSizeChange = (size) => {
    if (size === "all") {
      setPageSize(sortedInvoices.length);
    } else {
      setPageSize(Number(size));
    }
    setPage(1); // Reset to first page
  };

  const totalPages =
    pageSize === sortedInvoices.length
      ? 1
      : Math.max(1, Math.ceil(sortedInvoices.length / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const pageItems = sortedInvoices.slice(
    (pageSafe - 1) * pageSize,
    pageSafe * pageSize
  );

  // Build a condensed page list: show `1 2 3 ... last` when there are many pages
  const buildPageButtons = (current, total) => {
    // If there are only a few pages, show them all
    if (total <= 3) return Array.from({ length: total }, (_, i) => i + 1);
    // Otherwise show first three pages, ellipsis, and the last page
    return [1, 2, "...", total];
  };
  const pageButtons = buildPageButtons(pageSafe, totalPages);

  // Handler functions following Redux flow: component -> action -> saga -> API call -> reducer -> store
  const handleViewInvoice = (invoice) => {
    // set a local prop so modal can render immediately while details load
    setSelectedInvoiceProp(invoice);
    dispatch({
      type: INVOICE_DETAIL_REQUEST,
      payload: { id: invoice.id || invoice.invoice_id },
    });
    setModalOpen(true);
  };

  const editClickCount = useRef(0);
  const handleEditInvoice = (invoice) => {
    editClickCount.current += 1;

    dispatch({
      type: INVOICE_DETAIL_REQUEST,
      payload: { id: invoice.id || invoice.invoice_id },
    });

    setEditInvoiceModalOpen(true);
    setPendingEdit(true);
  };

  useEffect(() => {
    if (pendingEdit && selectedInvoice) {
      console.log(
        "[InvoiceTable] Detail loaded for edit, ensuring modal open. selectedInvoice=",
        selectedInvoice
      );
      // ensure modal is open
      setEditInvoiceModalOpen(true);
      setPendingEdit(false);
    }
  }, [pendingEdit, selectedInvoice]);

  // Extra debug: log every time modal open state changes
  useEffect(() => {
    console.log("[InvoiceTable] editInvoiceModalOpen=", editInvoiceModalOpen);
  }, [editInvoiceModalOpen]);

  const handleDeleteInvoice = (invoice) => {
    setDeletePrompt({ show: true, invoice });
  };

  const confirmDelete = () => {
    if (!deletePrompt.invoice) return;
    const id = deletePrompt.invoice.id || deletePrompt.invoice.invoice_id;
    const idStr = String(id);
    setDeleteRequested(idStr);
    dispatch({ type: DELETE_INVOICE_REQUEST, payload: { invoice_id: id } });
    setDeletePrompt({ show: false, invoice: null });
  };

  const cancelDelete = () => setDeletePrompt({ show: false, invoice: null });

  // Watch delete lifecycle to emit toast
  useEffect(() => {
    if (deleteRequested && !deleteLoading) {
      if (error) {
        showToast(error, "error");
      } else {
        // verify invoice removed
        const stillExists = invoices.some(
          (inv) => String(inv.id || inv.invoice_id) === String(deleteRequested)
        );
        // If we have an overrideInvoices list (project-specific), remove the deleted item from it
        setOverrideInvoices((prev) => {
          if (!Array.isArray(prev)) return prev;
          return prev.filter(
            (inv) =>
              String(inv.id || inv.invoice_id) !== String(deleteRequested)
          );
        });
        if (!stillExists)
          showToast(
            lastDeleteMessage || "Invoice deleted successfully",
            "success"
          );
      }
      setDeleteRequested(null);
    }
  }, [deleteLoading, error, deleteRequested, invoices]);

  const handleNewInvoice = () => {
    setNewInvoiceModalOpen(true);
    if (typeof onNewInvoice === "function") onNewInvoice();
  };

  const onSuccessInvoiceModal = () => {
    setNewInvoiceModalOpen(false);
    // Refresh invoices list after successful creation
    const params = {};
    if (clientId) params.client_id = clientId;
    if (projectId) params.project_id = projectId;
    dispatch({ type: FETCH_INVOICES_REQUEST, payload: params });
    // If we're viewing a project, also refresh the project-scoped invoices
    if (projectId) {
      (async () => {
        try {
          const resp = await Axios.get(
            `/invoices/invoice_list_by_project?project_id=${projectId}`
          );
          const data = resp?.data?.data || resp?.data || null;
          if (data) {
            // prefer explicit .invoices array when present
            if (Array.isArray(data.invoices))
              setOverrideInvoices(data.invoices);
            else if (Array.isArray(data)) setOverrideInvoices(data);
          }
        } catch (e) {
          console.debug(
            "Could not refresh project invoices after create",
            e?.message || e
          );
        }
      })();
    }
    showToast("Invoice created successfully", "success");
  };

  return (
    <div className="invoice-page-root">
      <Card className="invoice-card">
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              Error loading invoices: {error}
            </Alert>
          )}

          {/* Show loading spinner while fetching project invoices */}
          {projectId && projectFetchLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-2">Loading invoices...</p>
            </div>
          ) : projectId &&
            displayInvoices.length === 0 &&
            (projectFetchCompleted || !isLoading) ? (
            <div>
              <Row className="mb-4">
                <Col className="d-flex justify-content-end">
                  <Button
                    className="new-invoice-btn d-inline-flex align-items-center border-radius-40"
                    variant="primary"
                    onClick={handleNewInvoice}
                    disabled={createLoading}
                  >
                    <span className="new-invoice-icon" aria-hidden="true">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 5v14M5 12h14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="new-invoice-text">
                      {createLoading ? "Creating..." : "New Invoice"}
                    </span>
                  </Button>
                </Col>
              </Row>
              <div className="no-invoices-available text-center py-5">
                <h5>No Invoices Available</h5>
                <p className="text-muted">
                  There are no invoices for this project.
                </p>
              </div>
            </div>
          ) : (
            <div className="table-wrap">
              <Row className="mb-4">
                <Col className="d-flex align-items-center">
                  <span className="me-3 font-weight-500">
                    Last Synced:{" "}
                    {lastInvoiceSynced ? lastInvoiceSynced : "Never"}
                  </span>
                </Col>
                <Col className="d-flex justify-content-end">
                  <Button
                    className="new-invoice-btn d-inline-flex align-items-center border-radius-40"
                    variant="primary"
                    onClick={handleNewInvoice}
                    disabled={createLoading}
                  >
                    <span className="new-invoice-icon" aria-hidden="true">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 5v14M5 12h14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="new-invoice-text">
                      {createLoading ? "Creating..." : "New Invoice"}
                    </span>
                  </Button>
                </Col>
              </Row>
              <Table borderless className="invoice-table table-sm">
                <thead>
                  <tr>
                    <th>
                      <Dropdown className="d-inline">
                        <Dropdown.Toggle
                          as="button"
                          className="btn-link p-0 d-flex align-items-center gap-2"
                          id="sort-invoice-id"
                          style={{
                            color: "inherit",
                            textDecoration: "none",
                            fontSize: "inherit",
                          }}
                        >
                          <span>Invoice ID</span>
                          <i
                            className="bi bi-arrow-down-up"
                            style={{ fontSize: "12px" }}
                          ></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy("id");
                              setSortOrder("asc");
                              setPage(1);
                            }}
                          >
                            Sort A-Z
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy("id");
                              setSortOrder("desc");
                              setPage(1);
                            }}
                          >
                            Sort Z-A
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy(null);
                              setPage(1);
                            }}
                          >
                            Clear Sort
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </th>
                    <th>Invoice Title</th>
                    <th>
                      <Dropdown className="d-inline">
                        <Dropdown.Toggle
                          as="button"
                          className="btn-link p-0 d-flex align-items-center gap-2"
                          id="sort-invoice-date"
                          style={{
                            color: "inherit",
                            textDecoration: "none",
                            fontSize: "inherit",
                          }}
                        >
                          <span>Invoice Date</span>
                          <i
                            className="bi bi-arrow-down-up"
                            style={{ fontSize: "12px" }}
                          ></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy("date");
                              setSortOrder("asc");
                              setPage(1);
                            }}
                          >
                            Oldest First
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy("date");
                              setSortOrder("desc");
                              setPage(1);
                            }}
                          >
                            Newest First
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy(null);
                              setPage(1);
                            }}
                          >
                            Clear Sort
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </th>
                    <th>Due Date</th>
                    <th>Invoice Status</th>
                    <th className="text-right">
                      <Dropdown className="d-inline">
                        <Dropdown.Toggle
                          as="button"
                          className="btn-link p-0 d-flex align-items-center gap-2"
                          id="sort-amount"
                          style={{
                            color: "inherit",
                            textDecoration: "none",
                            fontSize: "inherit",
                          }}
                        >
                          <span>Before Tax Amount</span>
                          <i
                            className="bi bi-arrow-down-up"
                            style={{ fontSize: "12px" }}
                          ></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end">
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy("amount");
                              setSortOrder("asc");
                              setPage(1);
                            }}
                          >
                            Lowest First
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy("amount");
                              setSortOrder("desc");
                              setPage(1);
                            }}
                          >
                            Highest First
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy(null);
                              setPage(1);
                            }}
                          >
                            Clear Sort
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </th>
                    <th className="text-right">
                      <Dropdown className="d-inline">
                        <Dropdown.Toggle
                          as="button"
                          className="btn-link p-0 d-flex align-items-center gap-2"
                          id="sort-total-amount"
                          style={{
                            color: "inherit",
                            textDecoration: "none",
                            fontSize: "inherit",
                          }}
                        >
                          <span>Total Amount</span>
                          <i
                            className="bi bi-arrow-down-up"
                            style={{ fontSize: "12px" }}
                          ></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end">
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy("amount");
                              setSortOrder("asc");
                              setPage(1);
                            }}
                          >
                            Lowest First
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy("amount");
                              setSortOrder("desc");
                              setPage(1);
                            }}
                          >
                            Highest First
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              setSortBy(null);
                              setPage(1);
                            }}
                          >
                            Clear Sort
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </th>
                    <th>Paid Date</th>
                    <th>Payment Status</th>
                    <th className="text-center" style={{ width: "50px" }}></th>
                    <th className="text-center" />
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading invoices...
                      </td>
                    </tr>
                  ) : displayInvoices.length === 0 ? (
                    // For non-project contexts, show the default empty table row
                    <tr>
                      <td colSpan="10" className="text-center py-4">
                        No invoices found
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((invoice) => (
                      <InvoiceRow
                        key={invoice.id || invoice.invoice_id}
                        invoice={invoice}
                        STATUS_LABELS={STATUS_LABELS}
                        toISODateOnly={toISODateOnly}
                        handleViewInvoice={handleViewInvoice}
                        handleEditInvoice={handleEditInvoice}
                        handleDeleteInvoice={handleDeleteInvoice}
                        handleDownloadInvoice={handleDownloadInvoice}
                        downloadingInvoiceId={downloadingInvoiceId}
                        deleteLoading={deleteLoading}
                      />
                    ))
                  )}
                </tbody>
              </Table>
              {/* Rows per page selector - bottom right */}
              <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
                <span style={{ fontSize: "13px", color: "#6b7280" }}>Show</span>
                <select
                  className="form-select"
                  style={{ width: "100px", fontSize: "13px" }}
                  value={
                    pageSize === sortedInvoices.length &&
                    sortedInvoices.length > 500
                      ? "all"
                      : pageSize
                  }
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                >
                  <option value="100">100</option>
                  <option value="200">200</option>
                  <option value="500">500</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>
          )}

          {/* Invoice View/Edit Modal: render whenever modalOpen so it can open immediately */}
          <InvoiceModal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setSelectedInvoiceProp(null);
            }}
            invoice={selectedInvoice || selectedInvoiceProp}
            projectName={
              (selectedInvoice &&
                (selectedInvoice.project || selectedInvoice.project_name)) ||
              (selectedInvoiceProp &&
                (selectedInvoiceProp.project ||
                  selectedInvoiceProp.project_name)) ||
              projectName ||
              ""
            }
            onSwitchTab={onSwitchTab}
          />

          {/* New Invoice Modal */}
          <NewInvoiceModal
            show={newInvoiceModalOpen}
            onHide={() => setNewInvoiceModalOpen(false)}
            clientId={clientId}
            projectId={projectId}
            budgets={budgets}
            onToast={showToast}
            onSuccess={() => onSuccessInvoiceModal()}
          />

          {/* Edit Invoice Modal */}
          <EditInvoiceModal
            show={editInvoiceModalOpen}
            onHide={() => setEditInvoiceModalOpen(false)}
            onToast={showToast}
            onSuccess={() => {
              setEditInvoiceModalOpen(false);
              // Refresh invoices list after successful update
              const params = {};
              if (clientId) params.client_id = clientId;
              if (projectId) params.project_id = projectId;
              dispatch({ type: FETCH_INVOICES_REQUEST, payload: params });
              showToast("Invoice updated successfully", "success");
            }}
          />
          {/* Toast notification */}
          <Toast
            message={toast.message}
            type={toast.type}
            triggerId={toast.triggerId}
          />

          {/* Hidden PDF template for download */}
          {pdfInvoiceData && (
            <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
              <InvoicePdfTemplate
                id="invoice-table-download-template"
                invoiceNo={pdfInvoiceData.invoiceNo}
                invoiceDate={pdfInvoiceData.invoiceDate}
                subtotal={pdfInvoiceData.subtotal}
                gstAmount={pdfInvoiceData.gstAmount}
                total={pdfInvoiceData.total}
                dueDate={pdfInvoiceData.dueDate}
                clientName={pdfInvoiceData.clientName}
                contactName={pdfInvoiceData.contactName}
                clientAddress={pdfInvoiceData.clientAddress}
                invoiceTitle={pdfInvoiceData.invoiceTitle}
                description={pdfInvoiceData.description}
              />
            </div>
          )}

          {/* Delete confirmation overlay - use same modal style as Task delete */}
          {deletePrompt.show && (
            <div className="custom-project-modal">
              <div className="modal-backdrop fade show"></div>
              <div
                className="modal fade show"
                tabIndex="-1"
                aria-hidden="true"
                style={{ display: "block", zIndex: 1050 }}
              >
                <div
                  className="modal-dialog mx-auto"
                  style={{ marginTop: "15%" }}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Confirm Invoice Deletion</h5>
                    </div>
                    <div className="modal-body text-center">
                      <p>Are you sure you want to delete this invoice?</p>
                    </div>
                    <div className="d-flex justify-content-center gap-5 mb-3">
                      <button
                        className="btn btn-danger px-5"
                        onClick={confirmDelete}
                      >
                        Yes
                      </button>
                      <button
                        className="btn btn-secondary px-5"
                        onClick={cancelDelete}
                      >
                        No
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default InvoiceTable;
